import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Target, Play, RefreshCw, XCircle, Search,
  Users, BarChart3, Clock, CheckCircle, AlertTriangle, TestTube, Settings
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ConsensusPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState([]);
  const [searchSymbol, setSearchSymbol] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [config, setConfig] = useState({});
  const [configDirty, setConfigDirty] = useState(false);
  const [schedulerState, setSchedulerState] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [importUrl, setImportUrl] = useState("");
  const fileInputRef = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/price-targets/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setStats(result);
        setRunning(result.state?.status === "running");
        if (result.config) {
          setConfig(result.config);
        }
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchSchedulerState = useCallback(async () => {
    try {
      const response = await fetch(`${API}/price-targets/scheduler-state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setSchedulerState(result);
      }
    } catch (error) {
      console.error("Failed to fetch scheduler state:", error);
    }
  }, [token]);

  const fetchData = useCallback(async (symbol = "") => {
    try {
      let url = `${API}/price-targets/latest?limit=100`;
      if (symbol) {
        url = `${API}/price-targets/data?symbol=${symbol}&limit=50`;
      }
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setData(result.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchStats();
    fetchData();
    fetchSchedulerState();
    const interval = setInterval(() => {
      fetchStats();
      fetchSchedulerState();
    }, 5000);
    return () => clearInterval(interval);
  }, [token, fetchStats, fetchData, fetchSchedulerState]);

  const runPipeline = async (runType) => {
    try {
      const response = await fetch(`${API}/price-targets/run/${runType}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success(`${runType === "test" ? "Test" : "Daily"} consensus run started`);
        setRunning(true);
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.error || "Failed to start run");
      }
    } catch (error) {
      toast.error("Failed to start run");
    }
  };

  const cancelRun = async () => {
    try {
      const response = await fetch(`${API}/price-targets/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.info("Cancel signal sent");
      }
    } catch (error) {
      toast.error("Failed to cancel");
    }
  };

  const forceReset = async () => {
    if (!window.confirm("Force reset stuck consensus pipelines?")) return;
    try {
      const response = await fetch(`${API}/price-targets/force-reset`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Force reset complete");
        setRunning(false);
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to force reset");
    }
  };

  const saveConfig = async () => {
    try {
      const response = await fetch(`${API}/price-targets/config`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(config)
      });
      if (response.ok) {
        toast.success("Configuration saved");
        setConfigDirty(false);
        fetchSchedulerState();
      } else {
        toast.error("Failed to save configuration");
      }
    } catch (error) {
      toast.error("Failed to save configuration");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData(searchSymbol);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isJson = file.name.endsWith('.json') || file.name.endsWith('.json.gz');
    const isCsv = file.name.endsWith('.csv');

    if (!isJson && !isCsv) {
      toast.error("Please upload a CSV or JSON/JSON.gz file");
      return;
    }

    setUploading(true);
    
    // For CSV files, use chunked upload
    if (isCsv && file.size > 1024 * 1024) { // > 1MB
      await handleChunkedCsvUpload(file);
      return;
    }

    setUploadProgress({ status: "uploading", message: `Uploading ${file.name}...` });

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      let endpoint = `${API}/price-targets/import-json`;
      
      if (isCsv) {
        endpoint = `${API}/price-targets/import-csv`;
        formData.append('window_days', '180');
        formData.append('start_date', '2021-01-01');
        formData.append('end_date', '2026-04-14');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const result = await response.json();

      if (result.status === "completed") {
        toast.success(`Import complete! ${result.rows_loaded?.toLocaleString()} records loaded`);
        setUploadProgress({
          status: "completed",
          message: `Loaded ${result.rows_loaded?.toLocaleString()} records`,
          details: result
        });
        fetchStats();
        fetchData();
      } else if (result.status === "error") {
        toast.error(`Import failed: ${result.errors?.[0] || "Unknown error"}`);
        setUploadProgress({ status: "error", message: result.errors?.[0] || "Unknown error" });
      }
    } catch (error) {
      const errorMsg = error?.message || "Upload failed";
      toast.error(errorMsg);
      setUploadProgress({ status: "error", message: errorMsg });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleChunkedCsvUpload = async (file) => {
    const CHUNK_SIZE = 512 * 1024; // 512KB chunks
    const sessionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Read file as text and split by lines to preserve CSV structure
    const text = await file.text();
    const lines = text.split('\n');
    const header = lines[0];
    
    // Calculate chunks by lines, not bytes
    const linesPerChunk = Math.ceil(lines.length / Math.ceil(file.size / CHUNK_SIZE));
    const chunks = [];
    
    for (let i = 0; i < lines.length; i += linesPerChunk) {
      const chunkLines = i === 0 
        ? lines.slice(i, i + linesPerChunk)
        : [header, ...lines.slice(i, i + linesPerChunk)];
      chunks.push(chunkLines.join('\n'));
    }

    setUploadProgress({ 
      status: "uploading", 
      message: `Uploading ${file.name} in ${chunks.length} chunks...`,
      progress: 0
    });

    try {
      for (let i = 0; i < chunks.length; i++) {
        const formData = new FormData();
        const chunkBlob = new Blob([chunks[i]], { type: 'text/csv' });
        formData.append('file', chunkBlob, `chunk_${i}.csv`);
        formData.append('chunk_index', i.toString());
        formData.append('total_chunks', chunks.length.toString());
        formData.append('session_id', sessionId);
        formData.append('window_days', '180');
        formData.append('start_date', '2021-01-01');
        formData.append('end_date', '2026-04-14');

        const response = await fetch(`${API}/price-targets/import-csv-chunk`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });

        const result = await response.json();
        
        const progress = Math.round(((i + 1) / chunks.length) * 100);
        
        if (result.status === "chunk_received") {
          setUploadProgress({ 
            status: "uploading", 
            message: `Uploaded chunk ${i + 1}/${chunks.length}...`,
            progress
          });
        } else if (result.status === "completed") {
          toast.success(`Import complete! ${result.rows_loaded?.toLocaleString()} records loaded`);
          setUploadProgress({
            status: "completed",
            message: `Loaded ${result.rows_loaded?.toLocaleString()} records`,
            details: result
          });
          fetchStats();
          fetchData();
          break;
        } else if (result.status === "error") {
          throw new Error(result.errors?.[0] || "Chunk upload failed");
        }
      }
    } catch (error) {
      const errorMsg = error?.message || "Chunked upload failed";
      toast.error(errorMsg);
      setUploadProgress({ status: "error", message: errorMsg });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlImport = async () => {
    if (!importUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    setUploading(true);
    setUploadProgress({ status: "uploading", message: "Downloading and processing..." });

    try {
      // Determine endpoint based on file type
      const isCsv = importUrl.toLowerCase().endsWith('.csv');
      const endpoint = isCsv 
        ? `${API}/price-targets/import-csv-from-url?url=${encodeURIComponent(importUrl)}&start_date=2021-01-01&end_date=2026-04-14`
        : `${API}/price-targets/import-from-url?url=${encodeURIComponent(importUrl)}`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.status === "completed") {
        toast.success(`Import complete! ${result.rows_loaded?.toLocaleString()} records loaded`);
        setUploadProgress({
          status: "completed",
          message: `Loaded ${result.rows_loaded?.toLocaleString()} records`,
          details: result
        });
        fetchStats();
        fetchData();
        setImportUrl("");
      } else if (result.status === "error") {
        toast.error(`Import failed: ${result.errors?.[0] || "Unknown error"}`);
        setUploadProgress({ status: "error", message: result.errors?.[0] || "Unknown error" });
      }
    } catch (error) {
      const errorMsg = error?.message || "Import failed";
      toast.error(errorMsg);
      setUploadProgress({ status: "error", message: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  const handleSyncFromPreview = async () => {
    const previewUrl = "https://holdings-control.preview.emergentagent.com";
    
    setUploading(true);
    setUploadProgress({ status: "uploading", message: "Starting sync..." });

    try {
      // Start the sync job
      const startResp = await fetch(
        `${API}/price-targets/sync-from-preview?preview_url=${encodeURIComponent(previewUrl)}&batch_size=5000`,
        {
          method: 'POST',
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const startResult = await startResp.json();
      
      if (startResult.status !== "started") {
        throw new Error(startResult.errors?.[0] || "Failed to start sync");
      }
      
      setUploadProgress({ status: "uploading", message: "Sync started, checking progress..." });
      
      // Poll for status
      let completed = false;
      while (!completed) {
        await new Promise(r => setTimeout(r, 2000)); // Wait 2 seconds
        
        const statusResp = await fetch(`${API}/price-targets/sync-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const status = await statusResp.json();
        
        if (status.status === "completed") {
          completed = true;
          toast.success(`Sync complete! ${status.total_imported?.toLocaleString()} records`);
          setUploadProgress({
            status: "completed",
            message: `Synced ${status.total_imported?.toLocaleString()} records in ${status.batches_processed} batches`,
            details: status
          });
          fetchStats();
          fetchData();
        } else if (status.status === "error") {
          completed = true;
          throw new Error(status.errors?.[0] || "Sync failed");
        } else if (status.status === "cancelled") {
          completed = true;
          setUploadProgress({
            status: "error",
            message: "Sync cancelled",
            details: status
          });
        } else {
          // Still running - update progress
          const pct = status.progress_pct || 0;
          const imported = status.total_imported || 0;
          setUploadProgress({
            status: "uploading",
            message: `Syncing... ${pct}% (${imported.toLocaleString()} records)`,
            details: status
          });
        }
      }
    } catch (error) {
      if (error.message?.includes("postMessage")) {
        console.debug("Ignoring postMessage error");
      } else {
        const errorMsg = error?.message || "Sync failed";
        toast.error(errorMsg);
        setUploadProgress({ status: "error", message: errorMsg });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCancelSync = async () => {
    try {
      const response = await fetch(`${API}/price-targets/sync-cancel`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();
      if (result.status === "cancelled") {
        toast.success("Sync cancelled");
      } else {
        toast.error(result.message || "Could not cancel");
      }
    } catch (error) {
      toast.error("Failed to cancel sync");
    }
  };

  const getConsensusColor = (consensus) => {
    if (!consensus) return "bg-zinc-700";
    const c = consensus.toLowerCase();
    if (c.includes("strong buy")) return "bg-emerald-600";
    if (c === "buy") return "bg-green-600";
    if (c === "hold") return "bg-amber-600";
    if (c === "sell") return "bg-orange-600";
    if (c.includes("strong sell")) return "bg-red-600";
    return "bg-zinc-600";
  };

  const formatPrice = (price) => {
    if (!price) return "-";
    return `$${price.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const progress = stats?.state?.progress;

  return (
    <div className="space-y-6" data-testid="consensus-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Price Targets</h1>
          <p className="text-muted-foreground mt-1">
            Analyst price target consensus data
          </p>
        </div>
        <div className="flex items-center gap-2">
          {running ? (
            <>
              <Button variant="destructive" onClick={cancelRun} className="rounded-sm">
                <XCircle className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={forceReset}
                className="rounded-sm border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Force Reset
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => runPipeline("daily")} className="rounded-sm">
                <Play className="w-4 h-4 mr-2" />
                Daily Update
              </Button>
              <Button onClick={() => runPipeline("test")} className="rounded-sm">
                <TestTube className="w-4 h-4 mr-2" />
                Test Run
              </Button>
              <Button
                variant="outline"
                onClick={forceReset}
                className="rounded-sm border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                title="Force reset stuck pipelines"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      {running && progress && (
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                {progress.phase?.replace(/_/g, " ")}
              </span>
              <span className="text-sm text-muted-foreground">{progress.percent}%</span>
            </div>
            <div className="w-full bg-background rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress.percent}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>{progress.processed}/{progress.total} symbols</span>
              <span>{progress.succeeded} succeeded, {progress.failed} failed</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Target className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{stats?.symbols_with_data?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">Symbols with Targets</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <BarChart3 className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{stats?.total_records?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">Total Records</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Users className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{stats?.universe_count?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">Universe Size</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scheduler Status */}
      <Card className="border-emerald-500/20">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-emerald-500" />
              <div>
                <p className="font-medium">Daily Auto-Refresh</p>
                <p className="text-xs text-muted-foreground">
                  {schedulerState?.enabled 
                    ? `Scheduled at ${String(schedulerState?.hour_utc || 22).padStart(2, '0')}:${String(schedulerState?.minute || 0).padStart(2, '0')} UTC`
                    : "Disabled"
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {schedulerState?.next_run && (
                <span className="text-sm text-muted-foreground">
                  Next: {new Date(schedulerState.next_run).toLocaleString()}
                </span>
              )}
              <Badge variant={schedulerState?.enabled ? "default" : "secondary"}>
                {schedulerState?.enabled ? "Active" : "Disabled"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="overview" className="rounded-sm">Browse Data</TabsTrigger>
          <TabsTrigger value="runs" className="rounded-sm">Run History</TabsTrigger>
          <TabsTrigger value="import" className="rounded-sm">Sync Data</TabsTrigger>
          <TabsTrigger value="config" className="rounded-sm">Configuration</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Price Target Data</CardTitle>
                  <CardDescription>Browse analyst price targets and recommendations</CardDescription>
                </div>
                <form onSubmit={handleSearch} className="flex items-center gap-2">
                  <Input
                    placeholder="Search symbol..."
                    value={searchSymbol}
                    onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
                    className="w-32 rounded-sm"
                  />
                  <Button type="submit" variant="outline" size="icon" className="rounded-sm">
                    <Search className="w-4 h-4" />
                  </Button>
                  {searchSymbol && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => { setSearchSymbol(""); fetchData(); }}
                      className="rounded-sm"
                    >
                      Clear
                    </Button>
                  )}
                </form>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 font-medium">Symbol</th>
                      <th className="text-right py-2 px-3 font-medium">Avg Target</th>
                      <th className="text-right py-2 px-3 font-medium">Median</th>
                      <th className="text-right py-2 px-3 font-medium">High</th>
                      <th className="text-right py-2 px-3 font-medium">Low</th>
                      <th className="text-left py-2 px-3 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2 px-3 font-mono font-medium">{row.symbol}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatPrice(row.target_consensus)}</td>
                        <td className="py-2 px-3 text-right font-mono text-blue-400">{formatPrice(row.target_median)}</td>
                        <td className="py-2 px-3 text-right font-mono text-emerald-500">{formatPrice(row.target_high)}</td>
                        <td className="py-2 px-3 text-right font-mono text-red-500">{formatPrice(row.target_low)}</td>
                        <td className="py-2 px-3 text-muted-foreground">{row.date}</td>
                      </tr>
                    ))}
                    {data.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-muted-foreground">
                          No data available. Run a daily update to fetch price target data.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="runs" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Pipeline Runs</CardTitle>
              <CardDescription>Recent consensus data fetch runs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.recent_runs?.map((run) => (
                  <div 
                    key={run.run_id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      {run.status === "completed" ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      ) : run.status === "running" ? (
                        <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
                      ) : run.status === "failed" ? (
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-amber-500" />
                      )}
                      <div>
                        <p className="font-medium">
                          {run.run_type === "test" ? "Test Run" : "Daily Update"}
                          <span className="ml-2 text-xs text-muted-foreground font-mono">
                            #{run.run_id}
                          </span>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(run.started_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={
                        run.status === "completed" ? "default" : 
                        run.status === "running" ? "secondary" : 
                        "destructive"
                      }>
                        {run.status}
                      </Badge>
                      {run.records_saved > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {run.records_saved.toLocaleString()} saved
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                {(!stats?.recent_runs || stats.recent_runs.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No runs yet. Start a test or daily run to fetch consensus data.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="import" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Sync Data
              </CardTitle>
              <CardDescription>
                Transfer price target data from Preview to Production
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Status/Progress */}
              {uploadProgress && (
                <div className={`p-4 rounded-lg ${
                  uploadProgress.status === "completed" ? "bg-emerald-500/10 border border-emerald-500/30" :
                  uploadProgress.status === "error" ? "bg-red-500/10 border border-red-500/30" :
                  "bg-blue-500/10 border border-blue-500/30"
                }`}>
                  <div className="flex items-center gap-3">
                    {uploadProgress.status === "completed" ? (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    ) : uploadProgress.status === "error" ? (
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    ) : (
                      <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{uploadProgress.message}</p>
                      {uploadProgress.details && (
                        <div className="text-sm text-muted-foreground mt-2 space-y-2">
                          {/* Stats */}
                          {uploadProgress.details.batches_processed > 0 && (
                            <p>Batches: {uploadProgress.details.batches_processed} | Imported: {uploadProgress.details.total_imported?.toLocaleString()} | Matched: {uploadProgress.details.total_matched?.toLocaleString() || 0}</p>
                          )}
                          
                          {/* Progress bar */}
                          {uploadProgress.details.progress_pct > 0 && uploadProgress.details.progress_pct < 100 && (
                            <div className="w-full bg-black/20 rounded-full h-2">
                              <div 
                                className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                                style={{ width: `${uploadProgress.details.progress_pct}%` }}
                              />
                            </div>
                          )}
                          
                          {/* Logs */}
                          {uploadProgress.details.logs && uploadProgress.details.logs.length > 0 && (
                            <div className="bg-black/30 rounded p-2 max-h-40 overflow-y-auto font-mono text-xs space-y-0.5">
                              {uploadProgress.details.logs.slice(-10).map((log, i) => (
                                <div key={i} className={log.includes('error') || log.includes('failed') ? 'text-red-400' : 'text-gray-400'}>
                                  {log}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Errors */}
                          {uploadProgress.details.errors && uploadProgress.details.errors.length > 0 && (
                            <div className="bg-red-500/20 rounded p-2 text-xs text-red-400">
                              <strong>Errors:</strong>
                              {uploadProgress.details.errors.map((err, i) => (
                                <div key={i}>{err}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Sync from Preview */}
              <div className="p-6 border-2 border-emerald-500/30 bg-emerald-500/5 rounded-lg">
                <div className="flex flex-col gap-4">
                  <div>
                    <h4 className="text-lg font-medium text-emerald-400">Sync from Preview</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Local database has <strong>{stats?.total_records?.toLocaleString() || 0}</strong> records.
                      {uploadProgress?.details?.total_in_preview && (
                        <> Preview has <strong>{uploadProgress.details.total_in_preview.toLocaleString()}</strong> records.</>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleSyncFromPreview}
                      disabled={uploading}
                      size="lg"
                      className="flex-1 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-lg py-6"
                    >
                      {uploading ? (
                        <>
                          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2" />
                          Start Sync
                        </>
                      )}
                    </Button>
                    {uploading && (
                      <Button 
                        onClick={handleCancelSync}
                        size="lg"
                        variant="destructive"
                        className="rounded-sm text-lg py-6"
                      >
                        <XCircle className="w-5 h-5 mr-2" />
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="bg-muted/30 rounded-lg p-4 text-sm text-muted-foreground">
                <p><strong>How it works:</strong></p>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  <li>Fetches data from preview in batches of 5,000 records</li>
                  <li>Writes in sub-batches of 1,000 to avoid timeouts</li>
                  <li>Upserts to this database (no duplicates)</li>
                  <li>Retries failed batches automatically</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="config" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configuration
              </CardTitle>
              <CardDescription>Configure daily scheduler and pipeline settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Daily Scheduler */}
              <div className="space-y-4">
                <h3 className="font-medium">Daily Auto-Refresh</h3>
                
                <div className="flex items-center justify-between">
                  <Label htmlFor="daily-enabled">Enable daily automatic refresh</Label>
                  <Switch
                    id="daily-enabled"
                    checked={config.daily_refresh_enabled || false}
                    onCheckedChange={(checked) => {
                      setConfig({ ...config, daily_refresh_enabled: checked });
                      setConfigDirty(true);
                    }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hour (UTC)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={config.daily_refresh_hour_utc ?? 22}
                      onChange={(e) => {
                        setConfig({ ...config, daily_refresh_hour_utc: parseInt(e.target.value) || 0 });
                        setConfigDirty(true);
                      }}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Minute</Label>
                    <Input
                      type="number"
                      min={0}
                      max={59}
                      value={config.daily_refresh_minute ?? 0}
                      onChange={(e) => {
                        setConfig({ ...config, daily_refresh_minute: parseInt(e.target.value) || 0 });
                        setConfigDirty(true);
                      }}
                      className="rounded-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Pipeline Settings */}
              <div className="space-y-4">
                <h3 className="font-medium">Pipeline Settings</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Max Workers</Label>
                    <Input
                      type="number"
                      min={1}
                      max={50}
                      value={config.max_workers || 20}
                      onChange={(e) => {
                        setConfig({ ...config, max_workers: parseInt(e.target.value) || 20 });
                        setConfigDirty(true);
                      }}
                      className="rounded-sm"
                    />
                    <p className="text-xs text-muted-foreground">Parallel API workers</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Test Symbols Limit</Label>
                    <Input
                      type="number"
                      min={10}
                      max={500}
                      value={config.test_symbols_limit || 50}
                      onChange={(e) => {
                        setConfig({ ...config, test_symbols_limit: parseInt(e.target.value) || 50 });
                        setConfigDirty(true);
                      }}
                      className="rounded-sm"
                    />
                    <p className="text-xs text-muted-foreground">Symbols for test runs</p>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button 
                  onClick={saveConfig} 
                  disabled={!configDirty}
                  className="rounded-sm"
                >
                  Save Configuration
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ConsensusPage;
